'use client';

import React, { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  DOCUMENT_QUERY,
  type Document,
  type FavoriteItem,
  type FavoriteLabel,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { DocumentDetailDialog } from '@paeffgen/react/crm-documents/feature-documents';
import {
  SearchHighlightProvider,
  type TagColorId,
} from '@paeffgen/react/crm-documents/ui';
import { useResolveBelegByNumber } from '@paeffgen/react/crm-documents/util';
import {
  FavoriteActionSheet,
  FavoriteActionMenu,
} from './favorite-action-sheet';

interface UseFavoriteActionMenuArgs {
  /** Live-Favoritenliste — das Menü liest immer den aktuellen Artikel daraus. */
  favorites: FavoriteItem[];
  /** Volle Label-Objekte (für den Label-Editor im Menü). */
  labels: FavoriteLabel[];
  updateFavorite: (
    articleNumber: string,
    updates: { pinned?: boolean; labels?: string[] }
  ) => Promise<void> | void;
  toggleFavorite: (articleNumber: string) => Promise<void> | void;
  toggleLabelOnArticle: (articleNumber: string, label: string) => void;
  addUserLabel: (name: string, color?: string | null) => Promise<void>;
  /** Toggle „zur Anfrage" — Quelle der Wahrheit bleibt beim Aufrufer (Cart). */
  onAddToCart: (article: FavoriteItem) => void;
  /** Suchbegriff zum Highlighten im Beleg-Dialog (Standard: kein Highlight). */
  belegSearchTerm?: string;
}

interface UseFavoriteActionMenuReturn {
  /** An `FavoriteCard.onMenu` durchreichen (öffnet Sheet/Popover am Anker). */
  openActionMenu: (article: FavoriteItem, anchor: DOMRect) => void;
  /** Sheet (mobil) + Popover (Desktop) + Beleg-Dialog. Außerhalb von `space-y`
   *  rendern (fixed-Overlays sonst mit 16px-Lücke). */
  overlays: React.JSX.Element;
}

/**
 * Geteilte Orchestrierung für das Favoriten-Kebab-Menü (Aktions-Sheet/-Popover)
 * inkl. „letzte Rechnung als Beleg-Dialog öffnen". Wird von der Favoriten-Seite
 * und dem Dashboard genutzt, damit die Karten dort identisch funktionieren.
 */
export function useFavoriteActionMenu({
  favorites,
  labels,
  updateFavorite,
  toggleFavorite,
  toggleLabelOnArticle,
  addUserLabel,
  onAddToCart,
  belegSearchTerm = '',
}: UseFavoriteActionMenuArgs): UseFavoriteActionMenuReturn {
  const { adminQueryVars } = useAdmin();

  // Aktionsmenü (Sheet mobil / Popover Desktop). actionAnchor positioniert das
  // Popover. actionOpen steuert die Auf-/Zu-Animation; actionArticleNumber bleibt
  // während des Schließens erhalten, damit das Sheet sauber aus-animiert.
  const [actionOpen, setActionOpen] = useState(false);
  const [actionArticleNumber, setActionArticleNumber] = useState<string | null>(
    null
  );
  const [actionAnchor, setActionAnchor] = useState<DOMRect | null>(null);

  // Aktionsmenü liest die LIVE-Version (Label/Pin-Updates via Refetch sofort sichtbar).
  const actionArticle = actionArticleNumber
    ? favorites.find((f) => f.articleNumber === actionArticleNumber) ?? null
    : null;

  // ── Beleg-Modal (Preisinformation → letzte Rechnung) ─────────────────
  // Spiegelt die Logik der Artikelsuche: Belegnummer auflösen, Dokument via
  // cache-first laden und im DocumentDetailDialog anzeigen — ohne Routenwechsel.
  const apollo = useApolloClient();
  const { resolve: resolveBeleg } = useResolveBelegByNumber();
  const [openDocument, setOpenDocument] = useState<Document | null>(null);
  const [openedFromArticleNumber, setOpenedFromArticleNumber] = useState<
    string | null
  >(null);

  const handleOpenBeleg = useCallback(
    async (articleNumber: string, documentNumber: string) => {
      const resolved = await resolveBeleg(documentNumber);
      if (!resolved) return;
      try {
        const { data } = await apollo.query<{ document: Document | null }>({
          query: DOCUMENT_QUERY,
          variables: { id: resolved.id, ...adminQueryVars },
          fetchPolicy: 'cache-first',
        });
        if (data?.document) {
          setOpenDocument(data.document);
          setOpenedFromArticleNumber(articleNumber);
        } else {
          toast.warning(`Beleg ${documentNumber} nicht gefunden`);
        }
      } catch {
        toast.error(`Beleg ${documentNumber} konnte nicht geladen werden`);
      }
    },
    [apollo, adminQueryVars, resolveBeleg]
  );

  const handleCloseBeleg = useCallback(() => {
    setOpenDocument(null);
    setOpenedFromArticleNumber(null);
  }, []);

  const handleOpenLastBeleg = useCallback(() => {
    if (actionArticle?.lastDocumentNumber) {
      handleOpenBeleg(
        actionArticle.articleNumber,
        actionArticle.lastDocumentNumber
      );
    }
  }, [actionArticle, handleOpenBeleg]);

  const openActionMenu = useCallback(
    (article: FavoriteItem, anchor: DOMRect) => {
      setActionArticleNumber(article.articleNumber);
      setActionAnchor(anchor);
      setActionOpen(true);
    },
    []
  );
  // Nur die Animation schließen — actionArticleNumber/Anchor bleiben, damit das
  // Sheet seinen Inhalt während des Aus-Fahrens behält.
  const closeActionMenu = useCallback(() => setActionOpen(false), []);

  const handleCopyArticleNumber = useCallback(() => {
    if (actionArticle) {
      navigator.clipboard?.writeText(actionArticle.articleNumber);
      toast.success(`Artikelnummer ${actionArticle.articleNumber} kopiert`);
    }
  }, [actionArticle]);

  const handleTogglePinMenu = useCallback(() => {
    if (actionArticle) {
      updateFavorite(actionArticle.articleNumber, {
        pinned: !actionArticle.pinned,
      });
    }
  }, [actionArticle, updateFavorite]);

  const handleToggleLabelMenu = useCallback(
    (label: string) => {
      if (actionArticle) {
        toggleLabelOnArticle(actionArticle.articleNumber, label);
      }
    },
    [actionArticle, toggleLabelOnArticle]
  );

  const handleCreateLabelMenu = useCallback(
    (name: string, color: TagColorId) => {
      if (actionArticle) {
        addUserLabel(name, color).then(() =>
          toggleLabelOnArticle(actionArticle.articleNumber, name)
        );
      }
    },
    [actionArticle, addUserLabel, toggleLabelOnArticle]
  );

  const handleAddToCartMenu = useCallback(() => {
    if (actionArticle) onAddToCart(actionArticle);
  }, [actionArticle, onAddToCart]);

  const handleDeleteMenu = useCallback(() => {
    if (actionArticle) toggleFavorite(actionArticle.articleNumber);
  }, [actionArticle, toggleFavorite]);

  const overlays = (
    <>
      {/* Aktionsmenü — mobil Sheet, Desktop Popover */}
      <FavoriteActionSheet
        favorite={actionArticle}
        open={actionOpen}
        onClose={closeActionMenu}
        onOpenLastBeleg={handleOpenLastBeleg}
        onTogglePin={handleTogglePinMenu}
        onAddToCart={handleAddToCartMenu}
        onCopyArticleNumber={handleCopyArticleNumber}
        onDelete={handleDeleteMenu}
        labels={labels}
        activeLabels={actionArticle?.labels ?? []}
        onToggleLabel={handleToggleLabelMenu}
        onCreateLabel={handleCreateLabelMenu}
      />
      <FavoriteActionMenu
        favorite={actionArticle}
        open={actionOpen}
        anchor={actionAnchor}
        onClose={closeActionMenu}
        onOpenLastBeleg={handleOpenLastBeleg}
        onTogglePin={handleTogglePinMenu}
        onAddToCart={handleAddToCartMenu}
        onCopyArticleNumber={handleCopyArticleNumber}
        onDelete={handleDeleteMenu}
        labels={labels}
        activeLabels={actionArticle?.labels ?? []}
        onToggleLabel={handleToggleLabelMenu}
        onCreateLabel={handleCreateLabelMenu}
      />

      {/* Preisinformation → letzte Rechnung als Beleg-Detail anzeigen */}
      <SearchHighlightProvider searchTerm={belegSearchTerm}>
        <DocumentDetailDialog
          document={openDocument}
          isOpen={!!openDocument}
          onClose={handleCloseBeleg}
          highlightArticleNumber={openedFromArticleNumber}
        />
      </SearchHighlightProvider>
    </>
  );

  return { openActionMenu, overlays };
}
